"use client";

import { motion, useReducedMotion } from "framer-motion";
import { forwardRef, useEffect, useId, useRef, type KeyboardEvent, type PointerEventHandler } from "react";
import { useTranslation } from "react-i18next";
import Icon, { ICON_SIZES, type IconNameExtended } from "@/components/library/Icon";
import ShortcutKbd from "@/components/library/ShortcutKbd";

export type PreviewStarter = { id: string; icon: IconNameExtended; kind: "read" | "change"; text: string };

type GailLauncherPreviewProps = {
  heading: string;
  starters: readonly PreviewStarter[];
  shortcutHint?: string;
  /** Distance from the viewport bottom to the card's bottom edge. */
  bottom: number;
  /** Opened from the keyboard, so focus moves to the first starter. */
  focusFirstStarter: boolean;
  instantMotion?: boolean;
  onSelect: (starter: PreviewStarter) => void;
  onAskAnything: () => void;
  /** Escape closes the card and hands focus back to the launcher. */
  onEscape: () => void;
  onPointerEnter: PointerEventHandler<HTMLDivElement>;
  onPointerLeave: PointerEventHandler<HTMLDivElement>;
  onBlur: React.FocusEventHandler<HTMLDivElement>;
};

const rowClassName =
  "flex w-full items-center gap-2.5 px-3.5 text-left outline-none transition-colors hover:bg-200/40 focus-visible:bg-200/40 focus-visible:transition-none active:bg-200/70 active:transition-none";

/** Two starters for the current page above the launcher: one question about it, one change to it. */
const GailLauncherPreview = forwardRef<HTMLDivElement, GailLauncherPreviewProps>(function GailLauncherPreview(
  { heading, starters, shortcutHint, bottom, focusFirstStarter, instantMotion = false, onSelect, onAskAnything, onEscape, ...handlers },
  ref
) {
  const { t } = useTranslation("ask");
  const reducedMotion = useReducedMotion();
  const immediate = instantMotion || focusFirstStarter;
  const headingId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusFirstStarter) listRef.current?.querySelector("button")?.focus();
  }, [focusFirstStarter]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onEscape();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const buttons = Array.from(listRef.current?.querySelectorAll("button") ?? []);
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    event.preventDefault();
    buttons[(index + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    <motion.div
      ref={ref}
      role="group"
      aria-labelledby={headingId}
      initial={immediate ? false : { opacity: 0, y: reducedMotion ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit="exit"
      variants={{ exit: (fromKeyboard: boolean) => ({
        opacity: 0,
        y: reducedMotion || fromKeyboard || immediate ? 0 : 4,
        transition: { duration: fromKeyboard || immediate ? 0 : 0.12, ease: "easeOut" },
      }) }}
      transition={{ duration: immediate ? 0 : 0.12, ease: "easeOut" }}
      style={{ bottom }}
      onKeyDown={handleKeyDown}
      {...handlers}
      className="fixed right-6 z-40 flex w-80 flex-col overflow-hidden rounded-xl border border-neutral bg-panel shadow-[0_12px_32px_rgb(15_23_42/0.12)]"
    >
      <p id={headingId} className="px-3.5 pb-1.5 pt-3 text-xs text-600">
        {heading}
      </p>
      <div ref={listRef} className="flex flex-col">
        {starters.map((starter) => (
          <button
            key={starter.id}
            type="button"
            onClick={() => onSelect(starter)}
            className={`${rowClassName} py-2.5`}
          >
            <Icon icon={starter.icon} size={ICON_SIZES.XS} className="shrink-0 text-text_secondary" />
            <span className="min-w-0 flex-1 text-[13.5px] leading-5 text-900">{starter.text}</span>
            {starter.kind === "change" && (
              <span className="shrink-0 text-[11px] text-500">{t("launcher.asksFirst")}</span>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={onAskAnything}
          className={`${rowClassName} justify-between border-t border-neutral py-[9px] text-[12.5px] text-text_secondary`}
        >
          <span>{t("launcher.askAnything")}</span>
          {shortcutHint && <ShortcutKbd shortcut={shortcutHint} />}
        </button>
      </div>
    </motion.div>
  );
});

export default GailLauncherPreview;
